import React, { useState, useEffect } from "react";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button, InputField } from "@cakrawalajakarta/shared-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxOpen,
  faLocationDot,
  faChevronDown,
  faCar,
  faUser,
  faCheckCircle,
  faEdit,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";

const REGISTRATION_SOURCES = [
  {
    eventId: "JOTR2026",
    eventName: "Jakarta Open Trail Run 2026",
    collectionName: "pendaftaranJOTR2026",
  },
  {
    eventId: "FUNRUN5K2026",
    eventName: "Fun Run 5K Cibodas",
    collectionName: "pendaftaranFunRun5K2026",
  },
];

const PAID_STATUSES = new Set(["settlement", "capture", "paid"]);
const DECATHLON_LOCATION = "Jakarta Decathlon Semanggi";

export const RSVPRPC = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const db = getFirestore();
  const [selectedLocation, setSelectedLocation] = useState("");
  const [userData, setUserData] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [registrationCollection, setRegistrationCollection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasSelected, setHasSelected] = useState(false);
  const [suratPernyataan, setSuratPernyataan] = useState(null);

  // Lokasi RPC
  const locations = [
    {
      name: "Jakarta Decathlon Semanggi",
      date: "18-19/07/2026",
      time: "",
      mapLink:
        "https://www.google.com/maps/search/?api=1&query=Decathlon+Semanggi",
    },
    {
      name: "TNGP Cibodas",
      date: "24-25/07/2026",
      time: "",
      mapLink:
        "https://www.google.com/maps/search/?api=1&query=TNGP+Cibodas",
    },
  ];

  // Ambil data peserta dan surat pernyataan dari Firestore
  useEffect(() => {
    const fetchUserDataAndSurat = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Cari pendaftaran berbayar pada JOTR dan Fun Run. JOTR tetap
        // diprioritaskan jika satu akun memiliki pendaftaran pada kedua event.
        const registrationSnapshots = await Promise.all(
          REGISTRATION_SOURCES.map(async (source) => ({
            source,
            snapshot: await getDocs(
              query(
                collection(db, source.collectionName),
                where("uid", "==", user.uid)
              )
            ),
          }))
        );

        let paidRegistration = null;
        for (const { source, snapshot } of registrationSnapshots) {
          const registrationDoc = snapshot.docs.find((item) =>
            PAID_STATUSES.has(String(item.data().transaction_status || "").toLowerCase())
          );
          if (registrationDoc) {
            paidRegistration = { source, registrationDoc };
            break;
          }
        }

        if (paidRegistration) {
          const { source, registrationDoc } = paidRegistration;
          const data = registrationDoc.data();
          setOrderId(registrationDoc.id);
          setRegistrationCollection(source.collectionName);
          setUserData({
            eventId: source.eventId,
            eventName: source.eventName,
            namaLengkap: data.namaLengkap || "Pengguna",
            kategori: data.kategori || "-",
            bibNumber: data.bibNumber || "-",
            sizeJersey: data.sizeJersey || "-",
            rpcLocation: data.rpcLocation || "",
          });
          setSelectedLocation(data.rpcLocation || "");
          setHasSelected(!!data.rpcLocation);
        }

        // Ambil data surat pernyataan untuk event JOTR
        const suratQuery = query(
          collection(db, "suratPernyataan"),
          where("userId", "==", user.uid),
          where("eventId", "==", "JOTR")
        );
        const suratSnapshot = await getDocs(suratQuery);
        if (!suratSnapshot.empty) {
          let latestSurat = null;
          suratSnapshot.forEach((doc) => {
            const data = doc.data();
            if (
              !latestSurat ||
              (data.createdAt &&
                data.createdAt.seconds > latestSurat.createdAt?.seconds)
            ) {
              latestSurat = {
                downloadURL: data.downloadURL,
                fileName: data.fileName,
                namaLengkap: data.namaLengkap,
                createdAt: data.createdAt,
              };
            }
          });
          setSuratPernyataan(latestSurat);
        }
      } catch (err) {
        console.error("[ERROR] Mengambil data peserta atau surat:", err);
        showNotification(
          "error",
          "Failed to load participant or statement letter data. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchUserDataAndSurat();
  }, [user, db, showNotification]);

  // Handle pemilihan lokasi
  const handleLocationSelect = (e) => {
    setSelectedLocation(e.target.value);
  };

  // Handle submit pilihan lokasi
  const handleSubmit = async () => {
    if (!user?.uid) {
      showNotification(
        "error",
        "Please log in to select a race pack collection location."
      );
      navigate("/login");
      return;
    }

    if (!userData || !orderId || !registrationCollection) {
      showNotification(
        "error",
        "Registration data not found. Please contact the committee."
      );
      return;
    }

    if (!selectedLocation) {
      showNotification(
        "error",
        "Please select one of the race pack collection locations."
      );
      return;
    }

    if (selectedLocation === DECATHLON_LOCATION) {
      showNotification(
        "error",
        "Jakarta Decathlon Semanggi is no longer available. Please select TNGP Cibodas."
      );
      return;
    }

    setSubmitting(true);
    try {
      const orderRef = doc(db, registrationCollection, orderId);
      const updateData = {
        rpcLocation: selectedLocation,
        updatedAt: new Date(),
      };
      await updateDoc(orderRef, updateData);
      setHasSelected(true);
      showNotification(
        "success",
        `Location "${selectedLocation}" saved successfully!`
      );
    } catch (err) {
      console.error("[ERROR] Menyimpan pilihan lokasi:", err);
      showNotification(
        "error",
        "Failed to save the location. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Fungsi untuk mengedit pilihan
  const handleEditSelection = () => {
    setHasSelected(false);
  };

  // Fungsi untuk memformat tanggal
  const formatDate = (dateString) => {
    if (!dateString || dateString === "To Be Confirmed") return dateString;
    try {
      if (dateString.includes("-")) {
        const [range, month, year] = dateString.split("/");
        const monthLabel = new Date(
          `${year}-${month}-01T00:00:00`
        ).toLocaleDateString("en-US", { month: "short" });
        return `${range} ${monthLabel} ${year}`;
      }
      const [day, month, year] = dateString.split("/");
      const date = new Date(`${year}-${month}-${day}`);
      if (isNaN(date)) return "-";
      return date.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  // Tampilan formulir pemilihan lokasi
  const renderFormView = () => (
    <section
      id="rsvp-rpc"
      className="w-full min-h-screen py-20 text-center text-lime-600 flex-col justify-center relative bg-white"
    >
      <div className="relative z-0 container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-6xl mx-auto space-y-4">
            {/* RPC Info - Statis */}
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              viewport={{ once: true }}
            >
              {locations.map((location, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl px-6 py-8 md:py-12 border border-gray-200 flex flex-col items-center justify-center"
                >
                  <FontAwesomeIcon
                    icon={faBoxOpen}
                    className="text-4xl text-lime-600 mb-4"
                  />
                  <a
                    href={location.mapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg text-lime-600 font-bold mb-3 hover:underline flex items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faLocationDot} />
                    RPC Location {index + 1}
                  </a>
                  <p className="text-xl text-lime-800">{location.name}</p>
                  <p className="text-sm text-gray-600">
                    Date: {formatDate(location.date)}{" "}
                    {location.time ? `(${location.time})` : ""}
                  </p>
                </div>
              ))}
            </motion.div>

            {/* Keterangan */}
            <motion.div
              className="bg-white rounded-2xl border border-gray-200 px-4 py-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              viewport={{ once: true }}
            >
              <h4 className="text-lg md:text-xl font-bold text-lime-600 mb-4">
                Race Pack Collection Information & Required Documents
              </h4>
              <div className="text-base text-gray-600 max-w-5xl mx-auto text-center mb-6 space-y-3">
                <p>
                  Mandatory Gear Self-Declaration is required before race pack
                  collection. Please complete and sign the declaration form. The
                  Organizer may conduct random mandatory gear inspections before
                  the start, at checkpoints, aid stations, or at the finish line in
                  accordance with the Official Race Rules.
                </p>
                <p>
                  Race pack collection can be done at one of the available
                  locations: Decathlon Semanggi Jakarta or TNGP Cibodas. Please
                  select your preferred collection point.
                </p>
                <p>
                  Kindly bring your ID card, a health certificate (issued within 7
                  days before race day), and the required documents listed below.
                </p>
              </div>

              {/* Tampilan Desktop (Tabel) */}
              <div className="hidden md:block overflow-x-auto border border-gray-200 rounded-xl">
                <table className="w-full table-auto divide-y divide-gray-200 text-left">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Document Name</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Required For</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">Download Link</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 text-sm font-semibold text-gray-800">
                        Statement Letter / Waiver (Surat Pernyataan)
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        All Participants (Mandatory)
                      </td>
                      <td className="px-4 py-4 text-center">
                        <Button
                          variant="primary"
                          onClick={() =>
                            window.open(
                              suratPernyataan?.downloadURL ||
                                "https://firebasestorage.googleapis.com/v0/b/cakrawala-jakartakreatif.firebasestorage.app/o/waiver%2FWAIVER%20JOTR%202026.pdf?alt=media&token=8e1d38c9-a14c-4531-b19b-f93b080d9757",
                              "_blank",
                              "noopener,noreferrer"
                            )
                          }
                          className="gap-2"
                        >
                          <FontAwesomeIcon icon={faDownload} />
                          <span>Download File</span>
                        </Button>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 text-sm font-semibold text-gray-800">
                        PAR-Q (Kuesioner Kesiapan Aktivitas Fisik)
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        All Participants (Mandatory)
                      </td>
                      <td className="px-4 py-4 text-center">
                        <Button
                          variant="primary"
                          onClick={() =>
                            window.open(
                              "https://firebasestorage.googleapis.com/v0/b/cakrawala-jakartakreatif.firebasestorage.app/o/waiver%2FPAR-Q%20JOTR%202026.pdf?alt=media&token=4a9af95c-56ac-4dee-8e4f-b0b2dd285eb9",
                              "_blank",
                              "noopener,noreferrer"
                            )
                          }
                          className="gap-2"
                        >
                          <FontAwesomeIcon icon={faDownload} />
                          <span>Download File</span>
                        </Button>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 text-sm font-semibold text-gray-800">
                        Power of Attorney (Surat Kuasa)
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        Representative Only (If represented by someone else)
                      </td>
                      <td className="px-4 py-4 text-center">
                        <Button
                          variant="primary"
                          onClick={() =>
                            window.open(
                              "https://firebasestorage.googleapis.com/v0/b/cakrawala-jakartakreatif.firebasestorage.app/o/suratKuasa%2FPower%20of%20Attorney%20JOTR%202026.pdf?alt=media&token=05156826-f3b7-4a2b-b338-fa3dbccbb8dc",
                              "_blank",
                              "noopener,noreferrer"
                            )
                          }
                          className="gap-2"
                        >
                          <FontAwesomeIcon icon={faDownload} />
                          <span>Download File</span>
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Tampilan Mobile (Kartu Responsif) */}
              <div className="md:hidden flex flex-col gap-3 mt-4">
                <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 flex flex-col gap-3 text-left">
                  <div>
                    <h5 className="font-bold text-gray-800 text-base">
                      Statement Letter / Waiver (Surat Pernyataan)
                    </h5>
                    <p className="text-xs text-gray-600 mt-1">
                      <span className="font-semibold text-gray-700">Required For:</span> All Participants (Mandatory)
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    onClick={() =>
                      window.open(
                        suratPernyataan?.downloadURL ||
                          "https://firebasestorage.googleapis.com/v0/b/cakrawala-jakartakreatif.firebasestorage.app/o/waiver%2FWAIVER%20JOTR%202026.pdf?alt=media&token=8e1d38c9-a14c-4531-b19b-f93b080d9757",
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                    className="w-full gap-2"
                  >
                    <FontAwesomeIcon icon={faDownload} />
                    <span>Download File</span>
                  </Button>
                </div>

                <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 flex flex-col gap-3 text-left">
                  <div>
                    <h5 className="font-bold text-gray-800 text-base">
                      PAR-Q (Kuesioner Kesiapan Aktivitas Fisik)
                    </h5>
                    <p className="text-xs text-gray-600 mt-1">
                      <span className="font-semibold text-gray-700">Required For:</span> All Participants (Mandatory)
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    onClick={() =>
                      window.open(
                        "https://firebasestorage.googleapis.com/v0/b/cakrawala-jakartakreatif.firebasestorage.app/o/waiver%2FPAR-Q%20JOTR%202026.pdf?alt=media&token=4a9af95c-56ac-4dee-8e4f-b0b2dd285eb9",
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                    className="w-full gap-2"
                  >
                    <FontAwesomeIcon icon={faDownload} />
                    <span>Download File</span>
                  </Button>
                </div>

                <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 flex flex-col gap-3 text-left">
                  <div>
                    <h5 className="font-bold text-gray-800 text-base">
                      Power of Attorney (Surat Kuasa)
                    </h5>
                    <p className="text-xs text-gray-600 mt-1">
                      <span className="font-semibold text-gray-700">Required For:</span> Representative Only (If represented by someone else)
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    onClick={() =>
                      window.open(
                        "https://firebasestorage.googleapis.com/v0/b/cakrawala-jakartakreatif.firebasestorage.app/o/suratKuasa%2FPower%20of%20Attorney%20JOTR%202026.pdf?alt=media&token=05156826-f3b7-4a2b-b338-fa3dbccbb8dc",
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                    className="w-full gap-2"
                  >
                    <FontAwesomeIcon icon={faDownload} />
                    <span>Download File</span>
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Grid untuk dua container */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Container 1: Informasi Peserta */}
              <motion.div
                className="bg-white rounded-2xl border border-gray-200 px-4 py-8 flex flex-col justify-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                viewport={{ once: true }}
              >
                <h4 className="text-lg md:text-xl font-bold text-lime-600 mb-4">
                  Participant Information
                </h4>
                {loading ? (
                  <p className="text-base text-gray-600">
                    Loading participant data...
                  </p>
                ) : user && userData ? (
                  <div className="grid grid-cols-1 gap-4">
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 text-left">
                      <p className="text-sm text-slate-500 font-bold mb-1">
                        Event:
                      </p>
                      <p className="text-base text-slate-800 font-semibold">
                        {userData.eventName}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 text-left">
                      <p className="text-sm text-slate-500 font-bold mb-1">
                        Participant Name:
                      </p>
                      <p className="text-base text-slate-800 font-semibold">
                        {userData.namaLengkap}
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 text-left">
                        <p className="text-sm text-slate-500 font-bold mb-1">
                          Category:
                        </p>
                        <p className="text-base text-slate-800 font-semibold uppercase">
                          {userData.kategori}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 text-left">
                        <p className="text-sm text-slate-500 font-bold mb-1">
                          Jersey Size:
                        </p>
                        <p className="text-base text-slate-800 font-semibold uppercase">
                          {userData.sizeJersey || "-"}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : !user ? (
                  <div className="text-center py-4">
                    <p className="text-base text-gray-600 mb-4">
                      Please log in to view your registration information and select your Race Pack Collection location.
                    </p>
                    <Button
                      onClick={() => navigate("/login")}
                      className="bg-lime-600 hover:bg-lime-700 text-white px-6 py-2.5 rounded-xl font-semibold shadow-sm"
                    >
                      Log In to Select Location
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-base text-gray-600">
                      No active registration found for your account. Only registered participants can select a Race Pack Collection location.
                    </p>
                  </div>
                )}
              </motion.div>

              {/* Container 2: Pemilihan Lokasi */}
              <motion.div
                className="bg-white rounded-2xl border border-gray-200 px-4 py-8 flex flex-col justify-between"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                viewport={{ once: true }}
              >
                <div>
                  <h4 className="text-lg md:text-xl font-bold text-lime-600 mb-4">
                    Select Collection Location
                  </h4>
                  <p className="text-base text-gray-600 mb-6">
                    If participants do not select a location, the race pack collection
                    will be assigned to TNGP Cibodas by default.
                  </p>
                  <div className="mb-6">
                    <InputField
                      type="select"
                      value={selectedLocation}
                      onChange={handleLocationSelect}
                      disabled={
                        loading ||
                        submitting ||
                        hasSelected ||
                        !user ||
                        !userData
                      }
                      options={[
                        { value: "", label: "-- Select Location --" },
                        {
                          value: DECATHLON_LOCATION,
                          label: "Jakarta Decathlon Semanggi (Unavailable)",
                          disabled: true,
                        },
                        {
                          value: "TNGP Cibodas",
                          label: "TNGP Cibodas (24-25 July 2026)",
                        },
                      ]}
                    />
                  </div>
                </div>
                <div>
                  {!hasSelected && (
                    <Button
                      onClick={handleSubmit}
                      loading={loading || submitting}
                      disabled={
                        !selectedLocation ||
                        selectedLocation === DECATHLON_LOCATION ||
                        !user ||
                        !userData
                      }
                      className="w-full"
                    >
                      Save Selection
                    </Button>
                  )}
                  {hasSelected && (
                    <Button
                      onClick={handleEditSelection}
                      className="w-full"
                    >
                      <FontAwesomeIcon icon={faEdit} className="mr-2" />
                      Change Location
                    </Button>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  return (
    <AnimatePresence mode="wait">
      {renderFormView()}
    </AnimatePresence>
  );
};
